// Extra Requests Components
import React, { useEffect, useState } from 'react';
import {
  getATOField,
  getATOIdInsideSet,
  getATOInsideSetField,
  RefATOField,
  RefATOInsideSetField,
  RefStoredValue,
  setATOFieldInsideSet,
  setStoredValue,
  StateStoredValue,
} from '../hooks/StoreHooks';
import { RefContextLanguage } from '../providers/LanguageProvider';
import CustomText from '../elements/CustomText';
import * as locCon from '../LocalConstants';
import * as libCon from '../community-hats-js-library/Constants';
import {
  checkSewaId,
  goToThirdPartyLink,
  isNullOrUndefined,
  trim,
} from '../community-hats-js-library/utils/generalFunctions';
import {
  SimpleCopyField,
  SimpleCopyFieldFromATOInsideSet,
  SimpleStoreCopyFieldFromATO,
} from '../elements/SimpleStoreCopyField';
import { SimpleStoreCopySelectionFieldFromATOInsideSet } from '../elements/SimpleStoreCopySelectionField';
import CustomButton from '../elements/CustomButton';
import { SyncATOButton, SyncATOInsideSetButton } from '../elements/SyncATOButton';
import { getPendingExtraRequests } from '../utils/extrasFunctions';
import CustomSpin from '../elements/CustomSpin';
import {
  formatDistanceShort,
  formatToStudyLocationReadable,
  nowInStudyLocation,
  nowISO,
} from '../community-hats-js-library/utils/dateFunctions';
import CollectionStatus from '../elements/CollectionStatus';
import { RefExtraRequestStatus } from '../hooks/BundleDeviceHooks';
import CheckboxGroup from '../elements/CheckboxGroup';
import { RefSetCurrentPage } from '../hooks/NavigationHooks';
import { InfoCircleOutlined } from '@ant-design/icons';
import { buildAirtableFormLink } from '../utils/generalFunctions';
import SimpleLink from '../elements/SimpleLink';

export const ExtraRequestViewer = ({ includeTitle = true }) => {
  const i18n = RefContextLanguage();

  // Pending Items and Last Extra Request Checked
  const [pendingItems, setPendingItems] = StateStoredValue(locCon.EXTRA_REQUEST_PENDING_ITEMS);
  const [lastExtraRequestCheck, setLastExtraRequestCheck] = StateStoredValue(locCon.LAST_EXTRA_REQUEST_CHECK);

  // Loading
  const [isLoading, setIsLoading] = useState(false);

  // Status and Message
  const [status, message] = RefExtraRequestStatus();

  const refreshPendingItems = async () => {
    setIsLoading(true);

    let newPenndingIems = await getPendingExtraRequests();
    setPendingItems(newPenndingIems);
    setLastExtraRequestCheck(nowISO());

    setIsLoading(false);
  };

  return (
    <div className="verticalSection">
      {includeTitle && <CustomText type={locCon.SECTION_TITLE_TEXT}>{i18n.t('extraRequests')}</CustomText>}

      <CollectionStatus status={status} message={message} />

      <SimpleCopyField
        title={i18n.t('lastRefreshed')}
        value={
          isNullOrUndefined(lastExtraRequestCheck) ? (
            i18n.t('never')
          ) : (
            <>
              {`${formatToStudyLocationReadable(lastExtraRequestCheck, true, false)}`}
              <br />
              {`(${formatDistanceShort(lastExtraRequestCheck)})`}
            </>
          )
        }
        enableCopy={false}
      />
      {isLoading ? (
        <CustomSpin type={locCon.BUTTON_SPINNER} />
      ) : (
        <CustomButton type={locCon.PRIMARY_BUTTON} onClick={refreshPendingItems}>
          {i18n.t('refresh')}
        </CustomButton>
      )}

      {pendingItems.map((item, i) => (
        <div
          style={{
            backgroundColor: 'var(--background-color-2)',
            marginTop: '1vh',
            marginBottom: '1vh',
            width: '95%',
          }}
          key={i}
        >
          <ExtraRequestItem item={item} />
        </div>
      ))}
    </div>
  );
};

export const ExtraRequestItem = ({ item }) => {
  switch (item) {
    case locCon.EXTRA_REQUEST_UPDATE_SENSOR_POSITION:
      return <UpdateSensorPlacementPositionsRequest />;
    case locCon.EXTRA_REQUEST_UPDATE_SENSOR_PHOTO:
      return <UpdateSensorPlacementPhotoRequest />;
    case locCon.EXTRA_REQUEST_UPDATE_MISSING_SEWA_ID:
      return <ParticipantIdUpdater />;
    case locCon.EXTRA_REQUEST_UPDATE_FITBIT_API_TOKEN:
      return <FitbitTokenNotAcive />;
    case locCon.EXTRA_REQUEST_UPDATE_GOOGLE_HEALTH_API_TOKEN:
      return <GoogleHealthTokenNotAcive />;
    default:
      console.error(`Extra Request Item ${item} not recognized`);
      return <div>Unkwon Extra Request</div>;
  }
};

export const UpdateSensorPlacementPositionsRequest = () => {
  const i18n = RefContextLanguage();

  const pendingPositionIds = RefStoredValue(locCon.EXTRA_REQUEST_UPDATE_SENSOR_POSITION_MISSING_IDS);

  return (
    <div className="verticalSection">
      <CustomText type={locCon.ELEMENT_TITLE_TEXT}>{i18n.t('pendingSensorPlacementPositions')}</CustomText>
      {pendingPositionIds.length > 0 ? (
        <div className="verticalSection">
          <CustomText type={locCon.INFO_TEXT}>{i18n.t('pendingSensorPlacementPositionsInstructions')}</CustomText>
          {pendingPositionIds.map((posId) => (
            <div
              key={posId}
              style={{
                border: '2px solid var(--primary-color-2)',
                borderRadius: '4%',
                backgroundColor: 'var(--background-color-2)',
                marginTop: '1vh',
                marginBottom: '1vh',
                width: '95%',
              }}
            >
              <CheckboxGroup
                disableClick={true}
                mainCheckbox={{
                  [libCon.ID]: locCon.getUpdateSensorPositionCheckBoxId(posId),
                  [libCon.TEXT]: i18n.t('updateSensorPosition'),
                }}
              />
              <UpdateSingleSensorPlacementPositions positionId={posId} />
            </div>
          ))}
        </div>
      ) : (
        <div className="verticalSection">
          <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('allSensorPositionsUpToDate')}</CustomText>
          <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('pleaseRefresh')}</CustomText>
        </div>
      )}
    </div>
  );
};

export const UpdateSingleSensorPlacementPositions = ({ positionId }) => {
  const i18n = RefContextLanguage();

  const newPosition = RefATOInsideSetField(positionId, locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT, libCon.ATF_POSITION);

  // If checked is false, set position to null and updates old position with current
  useEffect(() => {
    let currentPosition = getATOInsideSetField(
      positionId,
      locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT,
      libCon.ATF_POSITION
    );
    let currentPositionChecked = getATOInsideSetField(
      positionId,
      locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT,
      libCon.ATF_SENSOR_POSITION_CHECKED_STATUS
    );

    if (!isNullOrUndefined(currentPosition) && currentPositionChecked === libCon.ATF_PENDING) {
      setATOFieldInsideSet(positionId, locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT, libCon.ATF_POSITION, null);
      setATOFieldInsideSet(
        positionId,
        locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT,
        libCon.ATF_OLD_POSITION,
        currentPosition
      );
    }
  }, [positionId]);

  return (
    <div className="verticalSection">
      <div style={{ marginTop: 15 }}>
        <SimpleCopyFieldFromATOInsideSet
          title={i18n.t('serial')}
          atoId={locCon.AT_OBJECT_SENSOR}
          positionId={positionId}
          fieldId={libCon.ATF_SERIAL}
          enableCopy={false}
        />
      </div>
      <div className="horizontalSection">
        <div style={{ width: '50%' }}>
          <SimpleCopyFieldFromATOInsideSet
            title={i18n.t('brand')}
            atoId={locCon.AT_OBJECT_SENSOR}
            positionId={positionId}
            fieldId={libCon.ATF_BRAND}
            enableCopy={false}
          />
        </div>
        <div style={{ width: '50%' }}>
          <SimpleCopyFieldFromATOInsideSet
            title={i18n.t('model')}
            atoId={locCon.AT_OBJECT_SENSOR}
            positionId={positionId}
            fieldId={libCon.ATF_MODEL}
            enableCopy={false}
          />
        </div>
      </div>
      <SimpleStoreCopySelectionFieldFromATOInsideSet
        onClickCleanup={(val) => {
          if (!isNullOrUndefined(val)) {
            setATOFieldInsideSet(
              positionId,
              locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT,
              libCon.ATF_SENSOR_POSITION_CHECKED_STATUS,
              libCon.ATF_READY_FOR_REVISION
            );
            setATOFieldInsideSet(
              positionId,
              locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT,
              libCon.ATF_SENSOR_POSITION_CHECKED_DATE,
              nowInStudyLocation(true)
            );
          }
        }}
        title={i18n.t('senorPosition')}
        positionId={positionId}
        atoId={locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT}
        fieldId={libCon.ATF_POSITION}
        options={libCon.SENSOR_POSITIONS}
        optionsTextDict={libCon.SENSOR_POSITIONS.reduce((dict, item) => {
          dict[item] = i18n.t(item);
          return dict;
        }, {})}
      />
      <SyncATOInsideSetButton
        positionId={positionId}
        atoId={locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT}
        atoBehavior={libCon.ATO_BEHAVIOR_UPDATE}
        forceDisable={isNullOrUndefined(newPosition)}
        forceDisabledText={i18n.t('pleaseUpdateSensorPosition')}
        becomeTextOnSuccess={!isNullOrUndefined(newPosition)}
        onClickCleanup={(val) => setStoredValue(locCon.getUpdateSensorPositionCheckBoxId(positionId), val)}
      />
    </div>
  );
};

export function ParticipantIdUpdater() {
  const i18n = RefContextLanguage();

  const sewaId = RefATOField(locCon.AT_OBJECT_PARTICIPANT, libCon.ATF_SEWA_ID);

  return (
    <div className="verticalSection">
      <CheckboxGroup
        disableClick={true}
        mainCheckbox={{
          [libCon.ID]: locCon.EXTRA_REQUEST_UPDATE_MISSING_SEWA_ID_CHECK_BOX,
          [libCon.TEXT]: i18n.t('updateSewaId'),
        }}
      />
      <CustomText type={locCon.INSTRUCTIONS_TEXT}>{i18n.t('updateSewaIdInfo')}</CustomText>
      {isNullOrUndefined(sewaId) || sewaId === libCon.MISSING ? (
        <div className="verticalSection">
          <SimpleStoreCopyFieldFromATO
            includeCheckbox={true}
            atoId={locCon.AT_OBJECT_PARTICIPANT}
            fieldId={libCon.ATF_SEWA_ID}
            title={i18n.t('sewaId')}
            infoText={i18n.t('sewaIdText')}
            placeHolder={i18n.t('sewaId')}
            checkFunction={checkSewaId}
            defaultValue={libCon.MISSING}
            checkboxText={i18n.t('sewaIdCheckboxText')}
            cleanBeforeSavingFunction={trim}
          />
          <SyncATOButton
            atoId={locCon.AT_OBJECT_PARTICIPANT}
            atoBehavior={libCon.ATO_BEHAVIOR_UPDATE}
            onClickCleanup={(val) => val}
            forceDisable={isNullOrUndefined(sewaId) || sewaId === libCon.MISSING}
            forceDisabledText={i18n.t('pleaseUpdateTheSewaID')}
          />
        </div>
      ) : (
        <CustomText type={locCon.INFO_TEXT}>{i18n.t('successSewaIdUpdated')}</CustomText>
      )}
    </div>
  );
}

export function FitbitTokenNotAcive() {
  const i18n = RefContextLanguage();

  // Current Page
  const setCurrentPage = RefSetCurrentPage();

  const tokenStatus = RefATOField(locCon.AT_OBJECT_EMAIL_ACCOUNT, libCon.ATF_FITBIT_TOKEN_STATUS);

  useEffect(() => {
    setStoredValue(locCon.EXTRA_REQUEST_UPDATE_FITBIT_API_TOKEN_CHECK_BOX, tokenStatus === libCon.ATF_ACTIVE);
  }, [tokenStatus]);

  return (
    <div className="verticalSection">
      <CheckboxGroup
        disableClick={true}
        mainCheckbox={{
          [libCon.ID]: locCon.EXTRA_REQUEST_UPDATE_FITBIT_API_TOKEN_CHECK_BOX,
          [libCon.TEXT]: i18n.t('updateFitbitAPI'),
        }}
      />
      <CustomText type={locCon.INSTRUCTIONS_TEXT}>{i18n.t('tokenNotActive')}</CustomText>

      <SimpleCopyField
        title={i18n.t('fitbitTokenStatus')}
        value={isNullOrUndefined(tokenStatus) ? '---' : i18n.t(tokenStatus)}
        enableCopy={false}
      />

      {tokenStatus !== libCon.ATF_ACTIVE ? (
        <CustomButton type={locCon.PRIMARY_BUTTON} onClick={() => setCurrentPage(locCon.PAGE_TOOL_FITBIT_API_UPDATER)}>
          {i18n.t('updateFitbitAPI')}
        </CustomButton>
      ) : (
        <div></div>
      )}
    </div>
  );
}

export function GoogleHealthTokenNotAcive() {
  const i18n = RefContextLanguage();

  // Current Page
  const setCurrentPage = RefSetCurrentPage();

  const tokenStatus = RefATOField(locCon.AT_OBJECT_EMAIL_ACCOUNT, libCon.ATF_GOOGLE_TOKEN_STATUS);

  useEffect(() => {
    setStoredValue(locCon.EXTRA_REQUEST_UPDATE_GOOGLE_HEALTH_API_TOKEN_CHECK_BOX, tokenStatus === libCon.ATF_ACTIVE);
  }, [tokenStatus]);

  return (
    <div className="verticalSection">
      <CheckboxGroup
        disableClick={true}
        mainCheckbox={{
          [libCon.ID]: locCon.EXTRA_REQUEST_UPDATE_GOOGLE_HEALTH_API_TOKEN_CHECK_BOX,
          [libCon.TEXT]: i18n.t('updateGoogleHealthAPI'),
        }}
      />
      <CustomText type={locCon.INSTRUCTIONS_TEXT}>{i18n.t('googleTokenNotActive')}</CustomText>

      <SimpleCopyField
        title={i18n.t('googleTokenStatus')}
        value={isNullOrUndefined(tokenStatus) ? '---' : i18n.t(tokenStatus)}
        enableCopy={false}
      />

      {tokenStatus !== libCon.ATF_ACTIVE ? (
        <CustomButton
          type={locCon.PRIMARY_BUTTON}
          onClick={() => setCurrentPage(locCon.PAGE_TOOL_GOOGLE_HEALTH_API_UPDATER)}
        >
          {i18n.t('updateGoogleHealthAPI')}
        </CustomButton>
      ) : (
        <div></div>
      )}
    </div>
  );
}

export const UpdateSensorPlacementPhotoRequest = () => {
  const i18n = RefContextLanguage();

  const pendingPositionIds = RefStoredValue(locCon.EXTRA_REQUEST_UPDATE_SENSOR_PHOTO_IDS);

  return (
    <div className="verticalSection">
      <CustomText type={locCon.ELEMENT_TITLE_TEXT}>{i18n.t('pendingSensorPlacementPhoto')}</CustomText>
      {pendingPositionIds.length > 0 ? (
        <div className="verticalSection">
          <CustomText type={locCon.INFO_TEXT}>
            {`${i18n.t('pendingSensorPlacementPhotoInstructions')}`}
            <InfoCircleOutlined
              style={{ marginLeft: 15, color: 'var(--primary-color-5)', fontSize: 30 }}
              onClick={() => goToThirdPartyLink(libCon.WIKI_SENSOR_PLACEMENT_PICTURE)}
            />
          </CustomText>
          {pendingPositionIds.map((posId) => (
            <div
              key={posId}
              style={{
                border: '2px solid var(--primary-color-2)',
                borderRadius: '4%',
                backgroundColor: 'var(--background-color-2)',
                marginTop: '1vh',
                marginBottom: '1vh',
                width: '95%',
              }}
            >
              <CheckboxGroup
                disableClick={true}
                mainCheckbox={{
                  [libCon.ID]: locCon.getUpdateSensorPhotoCheckBoxId(posId),
                  [libCon.TEXT]: i18n.t('updateSensorPhoto'),
                }}
              />
              <UpdateSingleSensorPhoto positionId={posId} />
            </div>
          ))}
        </div>
      ) : (
        <div className="verticalSection">
          <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('allSensorPhotosUpToDate')}</CustomText>
          <CustomText type={locCon.SUCCESS_TEXT}>{i18n.t('pleaseRefresh')}</CustomText>
        </div>
      )}
    </div>
  );
};

export const UpdateSingleSensorPhoto = ({ positionId }) => {
  const i18n = RefContextLanguage();

  // Placement link
  const [placementLink, setPlacementLink] = useState(null);
  const [linkClicked, setLinkClicked] = useState(false);

  const photoRetakeStatus = RefATOInsideSetField(
    positionId,
    locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT,
    libCon.ATF_PHOTO_RETAKE_STATUS
  );

  useEffect(() => {
    const update = async () => {
      const vals = {
        [libCon.ATF_SENSOR_SERIAL]: getATOInsideSetField(positionId, locCon.AT_OBJECT_SENSOR, libCon.ATF_SERIAL),
        [libCon.ATF_HOUSE_NUMERIC_ID]: getATOField(locCon.AT_OBJECT_HOUSE, libCon.ATF_NUMERIC_ID),
        [libCon.ATF_SENSOR_PLACEMENT_ID]: getATOIdInsideSet(positionId, locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT),
      };

      const hidden = [libCon.ATF_SENSOR_PLACEMENT_ID];

      const link = buildAirtableFormLink(libCon.AIRTABLE_CONFIG[libCon.AT_TABLE_SENSOR_PHOTO_FORM], vals, hidden);

      setPlacementLink(link);
    };

    update();

    if (photoRetakeStatus === libCon.ATF_PENDING) {
      setATOFieldInsideSet(positionId, locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT, libCon.ATF_PLACEMENT_PHOTO, null);
    }
  }, [positionId, photoRetakeStatus]);

  return (
    <div className="verticalSection">
      <div style={{ marginTop: 15 }}>
        <SimpleCopyFieldFromATOInsideSet
          title={i18n.t('serial')}
          atoId={locCon.AT_OBJECT_SENSOR}
          positionId={positionId}
          fieldId={libCon.ATF_SERIAL}
          enableCopy={false}
        />
      </div>
      <div className="horizontalSection">
        <div style={{ width: '50%' }}>
          <SimpleCopyFieldFromATOInsideSet
            title={i18n.t('brand')}
            atoId={locCon.AT_OBJECT_SENSOR}
            positionId={positionId}
            fieldId={libCon.ATF_BRAND}
            enableCopy={false}
          />
        </div>
        <div style={{ width: '50%' }}>
          <SimpleCopyFieldFromATOInsideSet
            title={i18n.t('model')}
            atoId={locCon.AT_OBJECT_SENSOR}
            positionId={positionId}
            fieldId={libCon.ATF_MODEL}
            enableCopy={false}
          />
        </div>
      </div>

      <div className="verticalSection" style={{ marginTop: '2.5vh', marginBottom: '1.5vh' }}>
        <CustomText type={locCon.ELEMENT_TITLE_TEXT}>{i18n.t('sensorPhotoLink')}</CustomText>
        {placementLink !== null ? (
          <SimpleLink
            text={i18n.t('addSensorPlacement')}
            link={placementLink}
            onClickCleanup={() => setLinkClicked(true)}
          />
        ) : (
          <CustomText type={locCon.DANGER_TEXT}>{i18n.t('errorPleaseSyncBundle')}</CustomText>
        )}
      </div>

      <SyncATOInsideSetButton
        positionId={positionId}
        atoId={locCon.AT_OBJECT_SENSORS_HOUSE_PLACEMENT}
        atoBehavior={libCon.ATO_BEHAVIOR_PULL}
        forceDisable={!linkClicked}
        forceDisabledText={i18n.t('pleaseUpdateTheSensorPhoto')}
        becomeTextOnSuccess={photoRetakeStatus !== libCon.ATF_PENDING}
        onClickCleanup={(val) =>
          setStoredValue(locCon.getUpdateSensorPhotoCheckBoxId(positionId), photoRetakeStatus !== libCon.ATF_PENDING)
        }
      />
    </div>
  );
};
